import type { ObservableQuery } from '@apollo/client'

/**
 * Keeps a name-targeted refetch from waking a query that a component parked with `skip`.
 *
 * A refetch that targets queries by name matches `standby` (skipped) observables too — Apollo
 * filters them out only for the `'active'` shorthand. A skipped query still holds whatever
 * variables were last in scope — usually placeholders (an empty id, an unbounded search, or,
 * before sign-in completes, no auth) — and refetching it by name fires a real request with those
 * placeholders; the pre-sign-in case 401s and bounces the user to `/login`.
 *
 * Returning `true` is Apollo's own default path: every non-skipped query refetches as usual and is
 * still awaited under `awaitRefetchQueries`.
 *
 * Mutations get this from `defaultOptions.mutate`; `client.refetchQueries` reads no defaults, so
 * its call sites pass it explicitly (a `no-restricted-syntax` lint rule flags literal option objects that omit it).
 *
 * Permanent policy — never remove this in favor of letting skipped queries refetch. While the
 * variables are placeholders the refetch buys no freshness: the response caches under those
 * placeholders, and the query fetches anyway when it un-skips with its real ones. When a parked
 * query genuinely needs refreshing, name it with an explicit `{ query, variables }` object.
 */
export const excludeStandbyQueries = (observableQuery: ObservableQuery): boolean =>
  observableQuery.options.fetchPolicy !== 'standby'
